import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SignedAvatarImage } from '@/components/shared/SignedAvatarImage';
import { formatDistanceToNow } from 'date-fns';
import { Heart, MessageSquare, UserPlus, Mail, AtSign, Sparkles, Reply, Users } from 'lucide-react';
import { useMarkAsRead, NotificationWithActor } from '@/hooks/useNotifications';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

const TYPE_CONFIG: Record<string, { icon: React.ElementType; color: string }> = {
  post_like: { icon: Heart, color: 'text-pink-500' },
  post_comment: { icon: MessageSquare, color: 'text-blue-500' },
  comment_reply: { icon: Reply, color: 'text-cyan-500' },
  comment_like: { icon: Heart, color: 'text-pink-400' },
  connection_request: { icon: UserPlus, color: 'text-violet-500' },
  new_message: { icon: Mail, color: 'text-emerald-500' },
  mention: { icon: AtSign, color: 'text-orange-500' },
  matchup: { icon: Sparkles, color: 'text-amber-500' },
  cohort_post: { icon: Users, color: 'text-indigo-500' },
};

interface NotificationItemProps {
  notification: NotificationWithActor;
}

export function NotificationItem({ notification }: NotificationItemProps) {
  const markAsRead = useMarkAsRead();
  const navigate = useNavigate();
  const config = TYPE_CONFIG[notification.type] || TYPE_CONFIG.post_like;
  const TypeIcon = config.icon;
  const actor = notification.actor_profile;

  const handleClick = () => {
    if (!notification.is_read) {
      markAsRead.mutate(notification.id);
    }
    if (notification.type === 'matchup') {
      navigate('/matchups');
    } else if (notification.related_post_id) {
      navigate(`/post/${notification.related_post_id}`);
    } else if (notification.type === 'new_message' && notification.actor_id) {
      navigate(`/messages/${notification.actor_id}`);
    } else if (notification.type === 'connection_request') {
      navigate('/connections');
    }
  };

  return (
    <button
      onClick={handleClick}
      className={cn(
        'flex items-start gap-3 w-full px-4 py-3 text-left transition-colors hover:bg-muted/50',
        !notification.is_read && 'bg-primary/5'
      )}
    >
      <div className="relative flex-shrink-0">
        <Avatar className="h-10 w-10">
          <SignedAvatarImage src={actor?.avatar_url || undefined} />
          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
            {actor?.full_name?.charAt(0) || '?'}
          </AvatarFallback>
        </Avatar>
        <div className={cn('absolute -bottom-1 -right-1 p-0.5 rounded-full bg-card', config.color)}>
          <TypeIcon className="h-3 w-3" />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={cn('text-sm leading-snug', !notification.is_read ? 'font-semibold text-foreground' : 'text-foreground')}>
            <span className="font-semibold">{actor?.full_name || 'Someone'}</span>{' '}
            <span className="text-muted-foreground font-normal">{notification.title}</span>
          </p>
          <span className="text-xs text-muted-foreground whitespace-nowrap flex-shrink-0">
            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: false })}
          </span>
        </div>
        {actor?.role && actor?.company && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {actor.role} at {actor.company}
          </p>
        )}
        {notification.body && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {notification.body}
          </p>
        )}
      </div>

      {!notification.is_read && (
        <div className="flex-shrink-0 mt-2">
          <div className="h-2 w-2 rounded-full bg-primary" />
        </div>
      )}
    </button>
  );
}
